// Geo helpers — real lat/lng → 0-100% map coordinates for StumbleMap.
//
// Why: StumbleMap renders pins on a stylized 100×100% SVG viewBox (no tile
// fetch per ADR-0018). To honor real venue positions while keeping the
// hand-drawn aesthetic, we compute a bounding box from the input coords,
// pad it, and project each point to a percent pair the canvas can place.
//
// Latitude inversion: latitudes increase northward but screen-y increases
// southward, so the y mapping flips the axis.

export type Coord = { latitude: number; longitude: number }

export type Bounds = {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

/** Smallest range we allow before injecting synthetic padding. */
const MIN_RANGE = 0.001

/**
 * Compute a bounding box from a set of coordinates with fractional padding.
 *
 * @param coords  candidate points (nulls already filtered)
 * @param paddingFrac  fraction of the raw range to inflate each edge by
 *                     (e.g. 0.1 = 10% margin so pins don't kiss the edge)
 * @returns bounds, or `null` if <2 valid points (caller should fall back)
 */
export function computeBounds(coords: Coord[], paddingFrac = 0.1): Bounds | null {
  const valid = coords.filter(
    (c) => c != null && Number.isFinite(c.latitude) && Number.isFinite(c.longitude),
  )
  if (valid.length < 2) return null

  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  let minLng = Number.POSITIVE_INFINITY
  let maxLng = Number.NEGATIVE_INFINITY
  for (const c of valid) {
    if (c.latitude < minLat) minLat = c.latitude
    if (c.latitude > maxLat) maxLat = c.latitude
    if (c.longitude < minLng) minLng = c.longitude
    if (c.longitude > maxLng) maxLng = c.longitude
  }

  // Floor the range so co-located venues don't collapse to a divide-by-zero.
  let latRange = maxLat - minLat
  let lngRange = maxLng - minLng
  if (latRange < MIN_RANGE) {
    const mid = (minLat + maxLat) / 2
    minLat = mid - MIN_RANGE / 2
    maxLat = mid + MIN_RANGE / 2
    latRange = MIN_RANGE
  }
  if (lngRange < MIN_RANGE) {
    const mid = (minLng + maxLng) / 2
    minLng = mid - MIN_RANGE / 2
    maxLng = mid + MIN_RANGE / 2
    lngRange = MIN_RANGE
  }

  const latPad = latRange * paddingFrac
  const lngPad = lngRange * paddingFrac
  return {
    minLat: minLat - latPad,
    maxLat: maxLat + latPad,
    minLng: minLng - lngPad,
    maxLng: maxLng + lngPad,
  }
}

/** Clamp `n` into the inclusive [min, max] interval. */
function clamp(n: number, min: number, max: number): number {
  if (n < min) return min
  if (n > max) return max
  return n
}

/**
 * Convert a real coordinate to 0–100% map coordinates within `bounds`.
 * y is inverted: higher latitudes → smaller y (top of canvas).
 */
export function coordToPercent(coord: Coord, bounds: Bounds): { x: number; y: number } {
  const lngRange = bounds.maxLng - bounds.minLng
  const latRange = bounds.maxLat - bounds.minLat
  const x = lngRange > 0 ? ((coord.longitude - bounds.minLng) / lngRange) * 100 : 50
  const y = latRange > 0 ? ((bounds.maxLat - coord.latitude) / latRange) * 100 : 50
  return { x: clamp(x, 0, 100), y: clamp(y, 0, 100) }
}
